// Access to TechDocs static files from outside the doc reader.
//
// A doc may point `draeger.com/docs-icon` at an image in its own repo (`assets/logo.png`),
// which resolves to `/api/techdocs/static/docs/…` (see ./docsIcon). A browser loads that
// through a plain <img>, which carries cookies but no bearer token, so the page needs the
// Backstage user cookie for the `techdocs` plugin — the same one the TechDocs reader gets
// through @backstage/plugin-auth-react's CookieAuthRefreshProvider. That provider blocks
// rendering behind a spinner/error panel, which a listing page must not do just for card
// icons, so this asks for the cookie once per page load (shared by every card and tile),
// renews it before it expires, and simply reports failure so callers keep their glyph.
//
// Lives in isdp-common (not packages/app) so the landscape plugin shares the same request.
// React-only: callers pass in the Backstage APIs from useApi(discoveryApiRef / fetchApiRef).
import { useEffect, useState } from 'react';

/** The two Backstage APIs used (structurally typed, so this file needs no Backstage import). */
export interface TechDocsCookieApis {
  discoveryApi: { getBaseUrl: (pluginId: string) => Promise<string> };
  /** fetchApi — adds the Backstage token to the cookie request. */
  fetchApi: { fetch: typeof fetch };
}

export type TechDocsCookieState =
  | { status: 'loading' }
  | { status: 'ready'; techdocsBase: string }
  | { status: 'failed' };

let pending: Promise<string> | null = null;
let renewTimer: ReturnType<typeof setTimeout> | undefined;

/** Ask the techdocs backend for its user cookie; resolves to the techdocs base URL. */
export function ensureTechDocsCookie(apis: TechDocsCookieApis): Promise<string> {
  if (pending) return pending;
  const request = (async () => {
    const base = await apis.discoveryApi.getBaseUrl('techdocs');
    const res = await apis.fetchApi.fetch(`${base}/.backstage/auth/v1/cookie`, {
      credentials: 'include',
    });
    // 404 = a backend without cookie auth: its static files need no cookie.
    if (!res.ok && res.status !== 404) throw new Error(`techdocs cookie: ${res.status}`);
    if (res.ok) {
      const { expiresAt } = (await res.json()) as { expiresAt?: string };
      const ms = expiresAt ? new Date(expiresAt).getTime() - Date.now() : 0;
      if (renewTimer) clearTimeout(renewTimer);
      // Renew a minute before expiry, never sooner than 30 s from now.
      renewTimer = setTimeout(() => {
        pending = null;
        ensureTechDocsCookie(apis).catch(() => undefined);
      }, Math.max(ms - 60_000, 30_000));
    }
    return base;
  })();
  pending = request;
  // A failure is not cached: the next page that needs the cookie tries again.
  request.catch(() => {
    if (pending === request) pending = null;
  });
  return request;
}

/**
 * Make sure the TechDocs user cookie is set before rendering TechDocs-hosted images.
 * With `enabled: false` no request is made and the state stays 'loading'.
 */
export function useTechDocsCookie(
  apis: TechDocsCookieApis,
  enabled = true,
): TechDocsCookieState {
  const [state, setState] = useState<TechDocsCookieState>({ status: 'loading' });
  const { discoveryApi, fetchApi } = apis;

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    ensureTechDocsCookie({ discoveryApi, fetchApi }).then(
      techdocsBase => {
        if (!cancelled) setState({ status: 'ready', techdocsBase });
      },
      () => {
        if (!cancelled) setState({ status: 'failed' });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, discoveryApi, fetchApi]);

  return state;
}

/** Test hook: forget the shared cookie request between tests. */
export function resetTechDocsCookieForTests(): void {
  pending = null;
  if (renewTimer) clearTimeout(renewTimer);
  renewTimer = undefined;
}
